/* eslint-disable no-nested-ternary */
/* eslint-disable no-underscore-dangle */
import { generateTrackerLink } from "~helpers/generators";
import { stripSuffixSlash } from "~helpers/strings";

export function getTrackerName(operator, page, path) {
  const trackerLinks = operator ? Object.keys(operator.links) : [];
  let trackerName = "main";
  let tracker = "";
  for (let i = 0; i < trackerLinks.length; i += 1) {
    const trackerLink = trackerLinks[i].toLowerCase().replace(" ", "_");
    if (
      generateTrackerLink(operator, trackerLink, false, page.template) === path
    ) {
      tracker =
        operator.links[
          Object.keys(operator.links).find(
            (key) => key.toLowerCase() === trackerLink.toLowerCase()
          )
        ];
      trackerName = trackerLink.toLowerCase();
      break;
    }

    // Not sure if this code is being used or not. To check
    if (path.includes("ppc_fallback")) {
      // eslint-disable-next-line react/prop-types
      tracker = operator.links["PPC Fallback"];
      break;
    }
    tracker = operator.links.main;
  }

  return { name: trackerName, value: tracker };
}

function isJsonString(str) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

function deparam(paramStr) {
  const paramArr = paramStr.split("&");
  const paramObj = {};
  paramArr.forEach((e) => {
    const param = e.split("=");
    paramObj[param[0]] = decodeURIComponent(param[1]);
  });
  return paramObj;
}

function clean(obj) {
  for (const propName in obj) {
    if (obj[propName] === null || obj[propName] === undefined) {
      delete obj[propName];
    }
  }
  return obj;
}

export function filterCfKeys(data) {
  const allowedKeys = [
    'cf-iplongitude',
    'cf-iplatitude',
    'cf-ipcontinent',
    'cf-ipcity',
    'cf-region-code',
    'cf-region',
    'cf-postal-code',
    'cf-ipcountry',
  ];

  return Object.keys(data)
    .filter(key => allowedKeys.includes(key))
    .reduce((obj, key) => {
      obj[key] = data[key];
      return obj;
    }, {});
}


// Helper to parse cookies from headers
function parseCookies(headers) {
  const cookies = headers?.get("cookie");
  if (!cookies) return { affObject: JSON.stringify({}) };

  return cookies
    .split(";")
    .map((item) => item.split("="))
    .reduce((acc, [key, value]) => {
      acc[key.trim().replace('"', "")] = value;
      return acc;
    }, {});
};

// Helper to extract URL query parameters
function extractUrlParams(url) {
  if (!url || !url.includes("?")) return null;
  return deparam(url.split("?")[1]);
};

// Helper to construct URL parameters
function buildUrlParams(operator, trackerName, headers, cookie, extraParams) {
  const ip = headers?.get("x-real-ip") || headers?.get("host") || "127.0.0.1";
  const userAgent = headers?.get("user-agent") || null;
  const affObject = isJsonString(cookie.affObject) ? JSON.parse(cookie.affObject) : {};

  if (extraParams && extraParams.pt) affObject.module = extraParams.pt;

  return {
    site_id: process.env.SITE_ID,
    operator_short_name: operator.short_name,
    operator_type: operator.type,
    language: operator.market.split("_")[1],
    tracker_name: trackerName.name,
    market_short_code: operator.market,
    ip_address: ip,
    user_agent: userAgent,
    user_uuid: cookie.affUUID || null,
    facebook_browser_id: cookie._fbp || null,
    facebook_click_id: cookie._fbc || null,
    split_test_name: cookie.split_test_name || null,
    google_client_id: cookie._ga || null,
    split_test_variant: cookie.split_test_variant || null,
    pii_guard_id: cookie._gi || null,
    facebook_pixel_id: process.env.PIXEL_ID || null,
    cookie: JSON.stringify(cookie),
    ...(affObject || {}),
    ...(affObject && !affObject.referer ? { referer: headers?.get("referrer") } : {}),
    ...(extraParams || {}),
  };
};

// Main function
export async function getAffiliateLink(operator, path, page, headers, url) {
  try {
    const trackerName = await getTrackerName(operator, page, path);
    const cookie = parseCookies(headers);
    const extraParams = extractUrlParams(url);
    
    const urlParam = buildUrlParams(operator, trackerName, headers, cookie, extraParams);

    const queryString = new URLSearchParams(clean(urlParam)).toString();
    const trackingUrl = `${process.env.GATSBY_TRACKING_API_URL}?${queryString}`;

    const res = await fetch(trackingUrl, {
      headers: headers ? filterCfKeys(Object.fromEntries(headers)) : {},
    });

    // Parse the response
    const data = res.ok ? await res.json() : { success: false };
    return { props: data };
    
  } catch (error) {
    console.error("Error fetching affiliate link:", error);
    return { props: { success: false } };
  }
}

export function getTrackingAPIParams(
  pageTemplate,
  module,
  tracker,
  clickedElement,
  url,
  referer,
  modulePosition,
  itemPosition
) {
  const urlParams = {};
  let extraData = {};

  if (pageTemplate) {
    urlParams.page_type = pageTemplate;
  }
  if (module) {
    urlParams.module = module;
  }
  if (tracker !== "main") {
    urlParams.tracker_name = tracker;
  }

  if (clickedElement) {
    urlParams.clicked_element = clickedElement;
  }

  if (referer) {
    urlParams.http_referer = referer;
  }

  if (modulePosition) {
    urlParams.module_page_position = modulePosition;
  }

  if (itemPosition) {
    urlParams.module_click_position = itemPosition;
  }

  if (url && url.split("?").length === 2) {
    // eslint-disable-next-line prefer-destructuring
    urlParams.request_url = url.split("?")[0];
    extraData = deparam(url.split("?")[1]);

    if (extraData.msclkid) {
      extraData.microsoft_click_id = extraData.msclkid;
      delete extraData.msclkid;
    }

    if (extraData.gclid) {
      extraData.google_click_id = extraData.gclid;
      delete extraData.gclid;
    }
  } else if (url) {
    urlParams.request_url = url;
  }

  return { ...urlParams, ...extraData };
}

export function isTrackerLink(link = "") {
  const linkFormatMain = process.env.TRACKER_LINK_FORMAT_MAIN || "";
  let isValidTrackingLink = false;
  const sanitisedLink = stripSuffixSlash(
    link.replace(/^.*\/\/[^\/]+/, "")
  ).split("/");
  const splitFormat = linkFormatMain.split(",");

  for (let i = 0; i < splitFormat.length; i++) {
    const item = splitFormat[i];
    const itemName = item.match(/\[(.*)\]/);
    if (itemName && itemName.length >= 2 && sanitisedLink.length >= i) {
      if (itemName[1] === sanitisedLink[i]) {
        isValidTrackingLink = true;
      } else {
        isValidTrackingLink = false;
        break;
      }
    }
  }

  return isValidTrackingLink;
}

// Note: Check if the default link is savailable otherwise hide
export function trackerLinkActive(operator, tracker = "main") {
  if (!operator || !operator?.links) {
    return false;
  }
  return operator?.links[tracker] || operator?.links?.main;
}
